/*
  VYOR Recommended: three ranked Nivoda stones inside the ring builder's Stone step.

  Stones come from Nivoda Connect's own app proxy on this store (the same calls
  the Nivoda Feed makes), so the browser never talks to Nivoda and never sees
  cost prices. The rules are the ones Joseph approved on 13 October 2026
  (vyor-stones repo, src/rules.js and src/rank.js). Change numbers there and here.

  Loaded by snippets/vyor-recommended.liquid, which renders the
  <div data-vyor-picks data-shape="..."> box inside <vyor-ring-builder>.
*/
(() => {
  if (window.__vyorPicks) return;
  window.__vyorPicks = true;

  const PROXY = "/apps/diamond-proxy/api";
  const PAGE = 20;
  const MAX_PAGES = 8;
  const CARAT_BAND = 0.2;
  const RATES_URL = "https://cdn.shopify.com/s/javascripts/currencies.js";

  // ---------- Rules (copy of src/rules.js) ----------
  const PROPORTIONS = {
    round: { ratio: { allowed: [1.0, 1.01], ideal: [1.0, 1.01] }, depth: { allowed: [59.0, 62.5], ideal: [60.0, 62.0] }, table: { allowed: [53, 58], ideal: [54, 57] } },
    oval: { ratio: { allowed: [1.33, 1.5], ideal: [1.38, 1.45] }, depth: { allowed: [57, 63], ideal: [58, 62] }, table: { allowed: [53, 63], ideal: [54, 60] } },
    "cushion:square": { ratio: { allowed: [1.0, 1.08], ideal: [1.0, 1.05] }, depth: { allowed: [61, 68], ideal: [62, 67] }, table: { allowed: [58, 68], ideal: [61, 67] } },
    "cushion:elongated": { ratio: { allowed: [1.3, 1.5], ideal: [1.35, 1.45] }, depth: { allowed: [61, 68], ideal: [62, 67] }, table: { allowed: [58, 68], ideal: [61, 67] } },
    pear: { ratio: { allowed: [1.5, 1.75], ideal: [1.55, 1.7] }, depth: { allowed: [57, 64], ideal: [58, 62] }, table: { allowed: [53, 64], ideal: [55, 62] } },
    emerald: { ratio: { allowed: [1.35, 1.55], ideal: [1.4, 1.5] }, depth: { allowed: [60, 69], ideal: [61, 67] }, table: { allowed: [60, 70], ideal: [61, 69] } },
    "radiant:square": { ratio: { allowed: [1.0, 1.08], ideal: [1.0, 1.05] }, depth: { allowed: [60, 69], ideal: [61, 67] }, table: { allowed: [58, 70], ideal: [61, 69] } },
    "radiant:elongated": { ratio: { allowed: [1.3, 1.55], ideal: [1.35, 1.4] }, depth: { allowed: [60, 69], ideal: [61, 67] }, table: { allowed: [58, 70], ideal: [61, 69] } },
    asscher: { ratio: { allowed: [1.0, 1.05], ideal: [1.0, 1.03] }, depth: { allowed: [60, 70], ideal: [62, 68] }, table: { allowed: [58, 70], ideal: [61, 66] } },
    princess: { ratio: { allowed: [1.0, 1.05], ideal: [1.0, 1.03] }, depth: { allowed: [64, 76], ideal: [68, 75] }, table: { allowed: [63, 76], ideal: [67, 72] } },
    marquise: { ratio: { allowed: [1.8, 2.15], ideal: [1.9, 2.1] }, depth: { allowed: [57, 64], ideal: [58, 62] }, table: { allowed: [52, 64], ideal: [53, 60] } },
  };
  const VARIANTS = { cushion: ["square", "elongated"], radiant: ["square", "elongated"] };
  // Nivoda's shape names for each VYOR shape.
  const NIVODA_SHAPES = {
    round: ["ROUND"],
    oval: ["OVAL"],
    cushion: ["CUSHION", "CUSHION BRILLIANT", "CUSHION MODIFIED"],
    pear: ["PEAR"],
    emerald: ["EMERALD"],
    radiant: ["RADIANT"],
    asscher: ["ASSCHER"],
    princess: ["PRINCESS"],
    marquise: ["MARQUISE"],
  };
  const EXCELLENT = ["EX", "EXCELLENT", "ID", "IDEAL"];
  const NO_GRADE = ["NONE", "NA", "N/A", "-", "NG", "NOT GRADED", "UNKNOWN", "0"];
  const FLUORESCENCE = ["NON", "NONE", "FNT", "FAINT"];
  const LABS = ["IGI", "GIA"];
  const NEVER_CLARITY = ["IF", "FL"];
  const WEIGHTS = { proportions: 40, finish: 20, spread: 15, purity: 10, clarity: 10, media: 5 };
  const MIN_PICK_SCORE = 70;
  const MIN_PICK_PROPORTIONS = 25;
  // Best value must score at least this, so the cheapest pick still looks good.
  const MIN_VALUE_SCORE = 80;
  const MORE_COUNT = 6;

  const SHAPE_NAMES = { round: "Round", oval: "Oval", cushion: "Cushion", pear: "Pear", emerald: "Emerald", radiant: "Radiant", asscher: "Asscher", princess: "Princess", marquise: "Marquise" };

  // ---------- Helpers ----------
  const esc = (v) => String(v == null ? "" : v).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const up = (v) => (v == null || v === "" ? null : String(v).trim().toUpperCase());
  const num = (v) => {
    if (v == null || v === "") return null;
    const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const round1 = (n) => Math.round(n * 10) / 10;

  // "Asscher", "Elongated Cushion" -> { shape, variant }
  function readShape(text) {
    const t = String(text || "").toLowerCase();
    const shape = Object.keys(NIVODA_SHAPES).find((s) => t.includes(s)) || null;
    const variant = /elongated/.test(t) ? "elongated" : "square";
    return { shape, variant };
  }
  function proportionKey(shape, variant) {
    if (!VARIANTS[shape]) return shape;
    return `${shape}:${VARIANTS[shape].includes(variant) ? variant : "square"}`;
  }

  // ---------- Exchange rate (the same Shopify rates the Nivoda Feed sends) ----------
  let ratesPromise = null;
  function getRates() {
    const have = () => window.Currency && window.Currency.rates && window.Currency.rates.AUD;
    if (have()) return Promise.resolve(window.Currency.rates);
    if (!ratesPromise) {
      ratesPromise = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = RATES_URL;
        s.async = true;
        s.onload = () => (have() ? resolve(window.Currency.rates) : reject(new Error("no rates")));
        s.onerror = () => reject(new Error("rates failed"));
        document.head.appendChild(s);
      }).catch((e) => {
        ratesPromise = null;
        throw e;
      });
    }
    return ratesPromise;
  }

  // ---------- Nivoda Connect proxy ----------
  async function post(path, body) {
    const res = await fetch(`${PROXY}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`proxy ${res.status}`);
    return res.json();
  }

  async function countStones(query) {
    const d = await post("/count", { callFrom: "feed", query });
    return (d && d.data && d.data.diamonds_by_query_count) || 0;
  }

  async function feedPage(query, page) {
    const d = await post("/v2/feed", { showTarrifFreeFeeds: false, callFrom: "feed", query, offset: page * PAGE, order: { type: "price", direction: "ASC" } });
    return Array.isArray(d) ? d : (d && (d.data || d.items)) || [];
  }

  async function fetchStones(query) {
    const total = await countStones(query);
    if (!total) return [];
    const pages = Math.min(Math.ceil(total / PAGE), MAX_PAGES);
    const all = await Promise.all(Array.from({ length: pages }, (_, i) => feedPage(query, i).catch(() => [])));
    const seen = new Set();
    return all.flat().filter((s) => s && s.id && !seen.has(s.id) && seen.add(s.id));
  }

  function buildQuery(o, cut) {
    const rules = PROPORTIONS[proportionKey(o.shape, o.variant)];
    const [rMin, rMax] = rules.ratio.allowed;
    return {
      labgrown: true,
      shapes: NIVODA_SHAPES[o.shape],
      certificate_lab: LABS,
      color: [o.colour],
      sizes: { from: o.carat, to: Math.round((o.carat + CARAT_BAND) * 100) / 100 },
      clarity: [o.clarity],
      cut,
      polish: ["EX"],
      symmetry: ["EX"],
      flouresence: ["NON", "FNT"],
      // Square shapes can be listed as 0.99 when width is the longer side.
      ratio: { from: rMin <= 1 ? Math.round((1 / rMax) * 100) / 100 : rMin, to: rMax },
      labgrown_type: [],
    };
  }

  // Rounds must have Excellent or Ideal cut. Fancy shapes have no lab cut
  // grade, so the cut filter is left open (Joseph's decision, PR #7).
  async function searchStones(o) {
    if (o.shape === "round") return fetchStones(buildQuery(o, ["EX", "ID"]));
    const open = await fetchStones(buildQuery(o, []));
    if (open.length) return open;
    return fetchStones(buildQuery(o, ["FR", "GD", "VG", "EX", "ID"]));
  }

  // Creates the Shopify product for the stone (Nivoda's own add-to-cart step).
  async function createStone(id) {
    const rates = await getRates();
    const d = await post("/diamond", { is_set_in_jewellery_mount: false, product_id: id, rates });
    if (!d || !d.variantId) throw new Error("no variant");
    const v = d.data && d.data.variants && d.data.variants.nodes && d.data.variants.nodes[0];
    return { variantId: Number(d.variantId), price: v && num(v.price) != null ? Math.round(num(v.price) * 100) : null };
  }

  // ---------- Map a Feed stone to the fields rank.js uses ----------
  function parseDescription(html) {
    const out = {};
    const re = /<li>\s*([^:<]+):\s*([^<]*)<\/li>/gi;
    let m;
    while ((m = re.exec(html || ""))) out[m[1].trim().toLowerCase()] = m[2].trim();
    return out;
  }

  function toStone(raw, audRate) {
    const c = raw.certificate || {};
    const d = parseDescription(raw.description);
    const dims = String(d.measurements || "").match(/[\d.]+/g) || [];
    let ratio = num(d["l/w ratio"]);
    if (ratio != null && ratio > 0 && ratio < 1) ratio = Math.round((1 / ratio) * 100) / 100;
    const usd = num(raw.price);
    const aud = usd != null && audRate ? Math.round(usd / audRate) : null;
    const v360 = c.v360 && c.v360.url ? c.v360.url : null;
    return {
      offer_id: raw.id,
      lab: c.lab || d.certificate || null,
      shape: c.shape || d.shape || null,
      carats: num(c.carats != null ? c.carats : d.carat),
      colour: c.color || d.colour || null,
      clarity: c.clarity || d.clarity || null,
      cut: c.cut || d.cut || null,
      polish: d.polish || null,
      symmetry: d.symmetry || null,
      fluorescence: d.fluorescence || null,
      ratio,
      depth_pct: num(d.depth),
      table_pct: num(d.table),
      measurements_mm: dims.slice(0, 3).map(Number),
      video: v360,
      frames: c.v360 ? num(c.v360.frame_count) : null,
      image: raw.topImage || null,
      price: aud != null ? aud * 100 : null,
    };
  }

  // ---------- Ranking (copy of src/rank.js, minus fields the Feed lacks) ----------
  function gateReasons(s, o, rules) {
    const out = [];
    if (!LABS.includes(up(s.lab))) out.push("lab");
    const clarity = up(s.clarity);
    if (NEVER_CLARITY.includes(clarity)) out.push("clarity never");
    else if (o.clarity && clarity !== o.clarity) out.push("clarity");
    if (o.colour && up(s.colour) !== o.colour) out.push("colour");
    if (!EXCELLENT.includes(up(s.polish))) out.push("polish");
    if (!EXCELLENT.includes(up(s.symmetry))) out.push("symmetry");
    const cut = NO_GRADE.includes(up(s.cut)) ? null : up(s.cut);
    if (o.shape === "round" && !EXCELLENT.includes(cut)) out.push("cut");
    const fluo = up(s.fluorescence);
    if (fluo && !FLUORESCENCE.includes(fluo)) out.push("fluorescence");
    if (!s.video) out.push("video");
    if (s.carats == null || s.carats < o.carat - 1e-9 || s.carats > o.carat + CARAT_BAND + 1e-9) out.push("carat");
    for (const [field, key] of [["ratio", "ratio"], ["depth", "depth_pct"], ["table", "table_pct"]]) {
      const v = s[key];
      if (v == null) continue;
      const [min, max] = rules[field].allowed;
      if (v < min - 1e-9 || v > max + 1e-9) out.push(field);
    }
    return out;
  }

  function closeness(v, { allowed, ideal }) {
    if (v >= ideal[0] && v <= ideal[1]) return 1;
    const edge = (allowedEdge, idealEdge) => {
      const span = Math.abs(idealEdge - allowedEdge);
      return span === 0 ? 0 : Math.max(0, 1 - Math.abs(v - idealEdge) / span);
    };
    return v < ideal[0] ? edge(allowed[0], ideal[0]) : edge(allowed[1], ideal[1]);
  }

  function baseScore(s, o, rules) {
    const parts = [["ratio", "ratio"], ["depth", "depth_pct"], ["table", "table_pct"]].map(([f, k]) => (s[k] == null ? 0 : closeness(s[k], rules[f])));
    const proportions = (WEIGHTS.proportions * (parts[0] + parts[1] + parts[2])) / 3;
    const cut = NO_GRADE.includes(up(s.cut)) ? null : up(s.cut);
    const finish = o.shape === "round" ? (cut === "ID" || cut === "IDEAL" ? 20 : 14) : 20;
    const fluo = up(s.fluorescence);
    const purity = fluo === "NON" || fluo === "NONE" ? 4 : fluo ? 2 : 0;
    return { proportions: round1(proportions), finish, spread: 0, purity, clarity: WEIGHTS.clarity, media: s.video ? WEIGHTS.media : 0 };
  }

  function faceUpPerCarat(s) {
    const [l, w] = s.measurements_mm || [];
    return l && w && s.carats ? (l * w) / s.carats : null;
  }

  function rankStones(stones, o) {
    const rules = PROPORTIONS[proportionKey(o.shape, o.variant)];
    const passed = stones.filter((s) => !gateReasons(s, o, rules).length).map((stone) => ({ stone, breakdown: baseScore(stone, o, rules) }));
    const spreads = passed.map((p) => faceUpPerCarat(p.stone)).filter((v) => v != null);
    const lo = Math.min(...spreads);
    const hi = Math.max(...spreads);
    for (const p of passed) {
      const v = faceUpPerCarat(p.stone);
      const s = v == null ? 0 : hi > lo ? (v - lo) / (hi - lo) : 0.5;
      p.breakdown.spread = round1(WEIGHTS.spread * s);
      p.score = round1(Object.values(p.breakdown).reduce((a, b) => a + b, 0));
      p.price = p.stone.price;
      p.ppc = p.price != null && p.stone.carats ? p.price / p.stone.carats : null;
    }
    const pool = passed.filter((p) => p.score >= MIN_PICK_SCORE && p.breakdown.proportions >= MIN_PICK_PROPORTIONS && p.price != null);
    const used = new Set();
    const take = (sorted) => {
      const p = sorted.find((x) => !used.has(x.stone.offer_id));
      if (p) used.add(p.stone.offer_id);
      return p;
    };
    // Best value first (the cheapest stone with a strong score), then Top pick,
    // then Balanced from the stones priced between them, so the three always
    // read cheapest to dearest.
    const byPrice = (list) => [...list].sort((a, b) => a.price - b.price || b.score - a.score);
    const value = take(byPrice(pool.filter((p) => p.score >= MIN_VALUE_SCORE))) || take(byPrice(pool));
    const top = take([...pool].sort((a, b) => b.score - a.score || a.price - b.price));
    const between = value && top ? pool.filter((p) => p.price >= value.price && p.price <= top.price) : [];
    const perDollar = (list) => list.sort((a, b) => b.score / b.ppc - a.score / a.ppc);
    const balanced = take(perDollar(between.filter((p) => p.score >= value.score)));
    const picks = [];
    if (value) picks.push({ label: "Best value", why: "The lowest price that passes every VYOR check.", ...value });
    if (balanced) picks.push({ label: "Balanced", why: "The best quality for the money.", ...balanced });
    if (top) picks.push({ label: "Top pick", why: "Closest to ideal proportions for this shape.", ...top });
    picks.sort((a, b) => a.price - b.price);
    // A few more stones that pass every check, best score first, for "See more".
    const more = pool
      .filter((p) => !used.has(p.stone.offer_id))
      .sort((a, b) => b.score - a.score || a.price - b.price)
      .slice(0, MORE_COUNT)
      .map((p) => ({ label: "VYOR approved", why: "", more: true, ...p }));
    return { picks, more, checked: stones.length, passed: passed.length };
  }

  // ---------- Styles ----------
  const css = document.createElement("style");
  css.textContent = `
.vrb-picks{margin:20px 0 4px}
.vrb-picks__intro{margin:4px 0 12px;font-size:13px;line-height:1.45;opacity:.75}
.vrb-picks__list{display:grid;gap:10px}
.vrb-pick{display:grid;grid-template-columns:88px 1fr;gap:12px;padding:12px;border:1px solid rgba(0,0,0,.14);border-radius:10px;background:transparent;text-align:left}
.vrb-pick.is-chosen{border-color:currentColor;box-shadow:0 0 0 1px currentColor}
.vrb-pick__img{width:88px;height:88px;border-radius:8px;object-fit:cover;background:rgba(0,0,0,.05)}
.vrb-pick__label{display:inline-block;margin-bottom:2px;font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase}
.vrb-pick__title{display:block;font-size:14px;font-weight:600;line-height:1.3}
.vrb-pick__spec{display:block;margin-top:2px;font-size:12px;line-height:1.4;opacity:.75}
.vrb-pick__why{display:block;margin-top:4px;font-size:12px;line-height:1.4}
.vrb-pick__foot{display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;margin-top:8px}
.vrb-pick__price{font-size:15px;font-weight:600;margin-right:auto}
.vrb-pick__btn{padding:7px 12px;border:1px solid currentColor;border-radius:999px;background:transparent;font:inherit;font-size:12px;font-weight:600;cursor:pointer;color:inherit}
.vrb-pick__video{padding:0;border:0;background:none;font:inherit;font-size:12px;text-decoration:underline;cursor:pointer;color:inherit}
.vrb-picks__more{justify-self:start;margin-top:2px}
.vrb-picks__msg{margin:6px 0 0;font-size:13px;line-height:1.45}
.vrb-picks__modes{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:8px 0 4px}
.vrb-picks__modes .vrb-plan{text-align:left}
.vrb-picks__body{margin-top:18px}
@media (max-width:480px){.vrb-picks__modes{grid-template-columns:1fr}}
.vrb-v360{width:min(92vw,560px);padding:0;border:0;border-radius:12px;background:#fff}
.vrb-v360::backdrop{background:rgba(0,0,0,.55)}
.vrb-v360__stage{position:relative;display:block;width:100%;aspect-ratio:1/1;background:#fff;cursor:grab;touch-action:pan-y;user-select:none}
.vrb-v360__stage:active{cursor:grabbing}
.vrb-v360__img{display:block;width:100%;height:100%;object-fit:contain;pointer-events:none}
.vrb-v360__note{position:absolute;left:0;right:0;bottom:10px;margin:0;text-align:center;font-size:12px;color:#555}
.vrb-v360__close{position:absolute;top:8px;right:8px;z-index:1;width:34px;height:34px;border:0;border-radius:50%;background:rgba(255,255,255,.9);font-size:20px;line-height:1;cursor:pointer}
@media (max-width:480px){.vrb-pick{grid-template-columns:72px 1fr}.vrb-pick__img{width:72px;height:72px}}`;
  document.head.appendChild(css);

  // ---------- 360 viewer ----------
  // Nivoda's 360 is a set of still frames: <v360.url>/<n>.webp, n = 0 .. frame_count - 1.
  // The viewer preloads every few frames, spins on its own, and can be dragged.
  const SPIN_FRAMES = 72;
  const SPIN_MS = 70;
  let viewer = null;
  let spin = null;

  function open360(stone) {
    if (!viewer) {
      viewer = document.createElement("dialog");
      viewer.className = "vrb-v360";
      viewer.innerHTML = '<button type="button" class="vrb-v360__close" aria-label="Close">×</button><div class="vrb-v360__stage" role="img" aria-label="360° diamond view"><img class="vrb-v360__img" alt=""><p class="vrb-v360__note">Loading 360° view…</p></div>';
      viewer.addEventListener("click", (e) => {
        if (e.target === viewer || e.target.closest(".vrb-v360__close")) viewer.close();
      });
      viewer.addEventListener("close", () => {
        if (spin) spin.stop();
        spin = null;
      });
      document.body.appendChild(viewer);
    }
    if (spin) spin.stop();
    const stage = viewer.querySelector(".vrb-v360__stage");
    const img = viewer.querySelector(".vrb-v360__img");
    const note = viewer.querySelector(".vrb-v360__note");
    img.src = stone.image || "";
    note.hidden = false;
    note.textContent = "Loading 360° view…";
    if (typeof viewer.showModal === "function") viewer.showModal();
    else viewer.setAttribute("open", "");
    if (!stone.video || !stone.frames) {
      note.textContent = "360° view not available for this stone.";
      return;
    }
    spin = makeSpin(stone, stage, img, note);
  }

  function makeSpin(stone, stage, img, note) {
    const step = Math.max(1, Math.round(stone.frames / SPIN_FRAMES));
    const urls = [];
    for (let i = 0; i < stone.frames; i += step) urls.push(`${stone.video}/${i}.webp`);
    const loaded = new Array(urls.length).fill(false);
    let done = 0;
    let frame = 0;
    let timer = null;
    let dragX = null;
    let dragFrame = 0;
    let stopped = false;
    const show = (i) => {
      frame = ((i % urls.length) + urls.length) % urls.length;
      if (loaded[frame]) img.src = urls[frame];
    };
    const play = () => {
      clearInterval(timer);
      timer = setInterval(() => show(frame + 1), SPIN_MS);
    };
    urls.forEach((u, i) => {
      const im = new Image();
      im.onload = () => {
        if (stopped) return;
        loaded[i] = true;
        done += 1;
        if (i === 0) show(0);
        if (done === urls.length || done === Math.ceil(urls.length / 2)) {
          note.hidden = true;
          if (!timer && dragX == null) play();
        }
      };
      im.onerror = () => {
        if (stopped) return;
        done += 1;
        if (i === 0) note.textContent = "360° view not available for this stone.";
      };
      im.src = u;
    });
    const down = (e) => {
      dragX = e.clientX;
      dragFrame = frame;
      clearInterval(timer);
      timer = null;
      stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
    };
    const move = (e) => {
      if (dragX == null) return;
      const per = stage.clientWidth / urls.length;
      show(dragFrame - Math.round((e.clientX - dragX) / Math.max(per, 2)));
    };
    const up = () => {
      dragX = null;
    };
    stage.addEventListener("pointerdown", down);
    stage.addEventListener("pointermove", move);
    stage.addEventListener("pointerup", up);
    stage.addEventListener("pointercancel", up);
    return {
      stop() {
        stopped = true;
        clearInterval(timer);
        stage.removeEventListener("pointerdown", down);
        stage.removeEventListener("pointermove", move);
        stage.removeEventListener("pointerup", up);
        stage.removeEventListener("pointercancel", up);
      },
    };
  }

  // ---------- Builder integration ----------
  const fmtCarat = (c) => `${Number(c).toFixed(2)}ct`;
  const caratOf = (v) => num(String(v || "").replace(/ct$/i, ""));
  const grade = (v) => {
    const u = up(v);
    return u === "EX" ? "Excellent" : u === "ID" ? "Ideal" : v;
  };
  const fluoText = (v) => {
    const u = up(v);
    return u === "NON" || u === "NONE" ? "No fluorescence" : u === "FNT" || u === "FAINT" ? "Faint fluorescence" : v ? `${v} fluorescence` : "";
  };

  function patch(P) {
    if (P.__vyorPicks) return;
    P.__vyorPicks = true;

    const orig = {
      update: P.update,
      stoneAddon: P.stoneAddon,
      stoneText: P.stoneText,
      syncLab: P.syncLab,
      select: P.select,
      choosePath: P.choosePath,
      cartLines: P.cartLines,
      addToCart: P.addToCart,
      canLeaveStone: P.canLeaveStone,
      updateNextButton: P.updateNextButton,
    };

    // A chosen recommended stone replaces the gemologist sourcing price.
    P.stoneAddon = function () {
      if (this._pick && this.path === "lab" && !this.isSelect()) return this._pick.price;
      return orig.stoneAddon.call(this);
    };

    P.stoneText = function () {
      const p = this._pick;
      if (p && this.path === "lab" && !this.isSelect()) return `${fmtCarat(p.carats)} ${this._picksShapeName} lab diamond (${p.colour}/${p.clarity}, ${p.lab})`;
      return orig.stoneText.call(this);
    };

    // The "VYOR Select" ring variant already includes a stone, so with a
    // chosen stone the ring always uses the plain lab setting variant.
    P.syncLab = function () {
      if (!this._pick) return orig.syncLab.call(this);
      if (this.path !== "lab" || this.idx.stone < 0) return;
      const value = this.stoneValueFor("lab");
      if (!value || this.variant.options[this.idx.stone] === value) return;
      const v = this.resolve(this.idx.stone, value);
      if (v && v.id !== this.variant.id) {
        this.variant = v;
        this.syncNative();
      }
    };

    P.select = function (key, value) {
      if (["carat", "colour", "clarity"].includes(key) && this.state[key] !== value) this._pick = null;
      return orig.select.call(this, key, value);
    };

    P.choosePath = function (path) {
      if (path !== "lab") this._pick = null;
      return orig.choosePath.call(this, path);
    };

    // "Choose your stone" mode: the customer must pick one of the three.
    P.picksBlocking = function () {
      return this._picksMode === "picks" && !this._pick && this.path === "lab" && !!this.querySelector("[data-vyor-picks]") && !this.querySelector("[data-vyor-picks]").hidden;
    };

    P.canLeaveStone = function () {
      if (this.picksBlocking()) return false;
      return orig.canLeaveStone.call(this);
    };

    P.updateNextButton = function () {
      orig.updateNextButton.call(this);
      if (this.state.step !== "stone" || !this.picksBlocking()) return;
      const btn = this.querySelector("[data-vrb-next]");
      const msg = this.querySelector("[data-vrb-msg]");
      if (btn) btn.disabled = true;
      if (msg) {
        msg.hidden = false;
        msg.textContent = "Choose one of the recommended stones, or switch to Gemologist sourced.";
      }
    };

    P.picksMode = function (mode) {
      this._picksMode = mode === "picks" ? "picks" : "gemologist";
      if (this._picksMode === "gemologist") this._pick = null;
      this.syncLab();
      this.update();
    };

    // Swap the gemologist sourcing line for the Nivoda stone, in the same bundle.
    P.cartLines = function () {
      const lines = orig.cartLines.call(this);
      const p = this._pick;
      if (!p || !p.variantId || this.path !== "lab" || this.isSelect()) return lines;
      const ring = lines[lines.length - 1];
      const lab = this.labVariant();
      const addons = lines.slice(0, -1).filter((l) => !(lab && l.id === lab.id));
      const stone = {
        id: p.variantId,
        quantity: 1,
        properties: {
          For: this.data.title,
          _vrb: ring.properties._vrb,
          _vrb_qty: "1",
          _vrb_role: "stone",
          _detail_page_url: `${location.origin}${location.pathname}?id=${encodeURIComponent(p.offer_id)}&stoneType=diamond`,
        },
      };
      const all = [stone].concat(addons);
      ring.properties._vrb_needs = all.map((l) => l.id).join(",");
      return all.concat([ring]);
    };

    P.addToCart = async function () {
      const p = this._pick;
      if (!p || p.variantId || this._adding || !this.state.ringSize || this.path !== "lab") return orig.addToCart.call(this);
      this._cartError = "";
      this._adding = true;
      this.updateNextButton();
      try {
        const made = await createStone(p.offer_id);
        p.variantId = made.variantId;
        if (made.price != null && made.price !== p.price) p.price = made.price;
      } catch (e) {
        this._cartError = "Sorry, this diamond has just become unavailable. Please choose another stone.";
        this._triedNext = true;
        this._adding = false;
        this.update();
        return;
      }
      this._adding = false;
      return orig.addToCart.call(this);
    };

    P.update = function () {
      orig.update.call(this);
      try {
        this.picksSync();
      } catch (e) {}
    };

    P.picksSync = function () {
      const box = this.querySelector("[data-vyor-picks]");
      if (!box) return;
      if (this._picksShape === undefined) {
        const s = readShape(box.dataset.shape);
        this._picksShape = s.shape;
        this._picksVariant = s.variant;
        this._picksShapeName = SHAPE_NAMES[s.shape] || "";
      }
      if (!this._picksMode) this._picksMode = "gemologist";
      const st = this.state;
      const show = this._picksShape && this.path === "lab" && st.carat && st.colour && st.clarity;
      box.hidden = !show;
      // The gemologist button carries this text while the box is shown.
      const hint = this.querySelector('[data-vrb-sub="lab"] > .vrb-hint');
      if (hint) hint.hidden = !!show;
      const mode = this._picksMode;
      this.querySelectorAll("[data-vyor-mode]").forEach((b) => {
        const on = b.dataset.vyorMode === mode;
        b.setAttribute("aria-checked", on ? "true" : "false");
        b.tabIndex = on ? 0 : -1;
      });
      const body = box.querySelector("[data-vyor-picks-body]");
      if (body) body.hidden = mode !== "picks";
      const gemPrice = box.querySelector('[data-vyor-mode-price="gemologist"]');
      if (gemPrice) {
        const lab = this.labVariant();
        gemPrice.textContent = lab ? this.money(lab.price) : "";
      }
      if (!show) return;
      const opts = { shape: this._picksShape, variant: this._picksVariant, carat: caratOf(st.carat), colour: up(st.colour), clarity: up(st.clarity) };
      const key = `${opts.shape}|${opts.variant}|${opts.carat}|${opts.colour}|${opts.clarity}`;
      if (key !== this._picksKey) {
        this._picksKey = key;
        this.picksLoad(box, opts, key);
      } else {
        this.picksRender(box);
      }
    };

    P.picksLoad = async function (box, opts, key) {
      this._picks = null;
      this._picksState = "loading";
      this.picksRender(box);
      try {
        const [raw, rates] = await Promise.all([searchStones(opts), getRates()]);
        if (key !== this._picksKey) return;
        const stones = raw.map((r) => toStone(r, rates.AUD));
        const ranked = rankStones(stones, opts);
        this._picks = ranked.picks.concat(ranked.more);
        this._picksMore = false;
        this._picksState = "ready";
      } catch (e) {
        if (key !== this._picksKey) return;
        this._picksState = "error";
      }
      this.picksRender(box);
    };

    P.picksRender = function (box) {
      const list = box.querySelector("[data-vyor-picks-list]");
      const msg = box.querySelector("[data-vyor-picks-msg]");
      const state = this._picksState;
      const picks = this._picks || [];
      const chosen = this._pick && this._pick.offer_id;
      const pickPrice = box.querySelector('[data-vyor-mode-price="picks"]');
      if (pickPrice) {
        const prices = picks.filter((p) => !p.more).map((p) => p.stone.price).filter((v) => v != null);
        pickPrice.textContent = this._pick ? this.money(this._pick.price) : prices.length ? `from ${this.money(Math.min(...prices))}` : "";
      }
      if (state === "loading") {
        list.innerHTML = "";
        msg.hidden = false;
        msg.textContent = "Finding the best certified stones for this ring…";
        return;
      }
      if (state === "error" || !picks.length) {
        list.innerHTML = "";
        msg.hidden = false;
        msg.textContent =
          state === "error"
            ? "We couldn't load recommended stones just now. Our gemologist can still source one to this spec."
            : "No stone passes every VYOR check at this size right now. Our gemologist can source one to this spec.";
        return;
      }
      msg.hidden = true;
      const extra = picks.filter((p) => p.more).length;
      const open = this._picksMore || (this._pick && picks.some((p) => p.more && p.stone.offer_id === chosen));
      const sig = picks.map((p) => p.stone.offer_id).join(",") + "|" + chosen + "|" + open;
      if (list.dataset.sig === sig) return;
      list.dataset.sig = sig;
      const cards = picks
        .map((p, i) => {
          if (p.more && !open) return "";
          const s = p.stone;
          const on = s.offer_id === chosen;
          const dims = s.measurements_mm.length >= 2 ? `${s.measurements_mm[0]} × ${s.measurements_mm[1]} mm` : "";
          const props = [s.ratio != null ? `Ratio ${Number(s.ratio).toFixed(2)}` : "", s.depth_pct != null ? `Depth ${s.depth_pct}%` : "", s.table_pct != null ? `Table ${s.table_pct}%` : ""].filter(Boolean).join(" · ");
          return `<div class="vrb-pick${on ? " is-chosen" : ""}" data-pick="${i}">
  ${s.image ? `<img class="vrb-pick__img" src="${esc(s.image)}" alt="${esc(fmtCarat(s.carats))} ${esc(this._picksShapeName)} lab diamond, face up" loading="lazy" width="88" height="88">` : '<span class="vrb-pick__img"></span>'}
  <div>
    <span class="vrb-pick__label">${esc(p.label)}</span>
    <span class="vrb-pick__title">${esc(fmtCarat(s.carats))} ${esc(this._picksShapeName)} · ${esc(s.colour)} · ${esc(s.clarity)} · ${esc(s.lab)} certified</span>
    <span class="vrb-pick__spec">${esc(grade(s.polish))} polish and symmetry · ${esc(fluoText(s.fluorescence))}${dims ? ` · ${esc(dims)}` : ""}</span>
    ${props ? `<span class="vrb-pick__spec">${esc(props)}</span>` : ""}
    ${p.why ? `<span class="vrb-pick__why">${esc(p.why)}</span>` : ""}
    <div class="vrb-pick__foot">
      <span class="vrb-pick__price">${esc(this.money(s.price))}</span>
      <button type="button" class="vrb-pick__video" data-pick-video="${i}">Watch 360°</button>
      <button type="button" class="vrb-pick__btn vrb-pick__choose" data-pick-choose="${i}" aria-pressed="${on}">${on ? "Selected ✓" : "Choose this stone"}</button>
    </div>
  </div>
</div>`;
        })
        .join("");
      const toggle = extra
        ? `<button type="button" class="vrb-pick__video vrb-picks__more" data-pick-more>${open ? "Show fewer stones" : `See ${extra} more VYOR approved stones`}</button>`
        : "";
      list.innerHTML = cards + toggle;
    };

    P.picksChoose = function (i) {
      const p = this._picks && this._picks[i];
      if (!p) return;
      const s = p.stone;
      this._picksMode = "picks";
      this._pick = this._pick && this._pick.offer_id === s.offer_id ? null : { ...s, label: p.label };
      this.syncLab();
      this.update();
    };
  }

  function init(el) {
    const box = el.querySelector("[data-vyor-picks]");
    if (el.__vyorPicksBound || !box) return;
    el.__vyorPicksBound = true;
    const sub = el.querySelector('[data-vrb-sub="lab"]');
    if (sub && !sub.contains(box)) sub.insertBefore(box, sub.querySelector(".vrb-own"));
    el.addEventListener("click", (e) => {
      const t = e.target;
      const choose = t.closest && t.closest("[data-pick-choose]");
      if (choose) {
        e.stopPropagation();
        el.picksChoose(Number(choose.dataset.pickChoose));
        return;
      }
      const video = t.closest && t.closest("[data-pick-video]");
      if (video) {
        e.stopPropagation();
        const p = el._picks && el._picks[Number(video.dataset.pickVideo)];
        if (p) open360(p.stone);
        return;
      }
      const more = t.closest && t.closest("[data-pick-more]");
      if (more) {
        e.stopPropagation();
        el._picksMore = !el._picksMore;
        el.picksRender(el.querySelector("[data-vyor-picks]"));
        return;
      }
      const mode = t.closest && t.closest("[data-vyor-mode]");
      if (mode) {
        e.stopPropagation();
        el.picksMode(mode.dataset.vyorMode);
      }
    });
    if (el.data) el.update();
  }

  customElements.whenDefined("vyor-ring-builder").then(() => {
    const Ctor = customElements.get("vyor-ring-builder");
    patch(Ctor.prototype);
    const run = () => document.querySelectorAll("vyor-ring-builder").forEach(init);
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  });
})();
